import * as React from 'react';
import { DefaultButton, Stack } from '@fluentui/react';
import type { Service } from '../../../interfaces/Service';
import type {
    AgentTaskPullRequestSummary,
    AgentTaskWorkItemSummary,
} from '../types';
import {
    buildAdoPullRequestUrl,
    getPullRequestStatusWithHealthLabel,
    stopSelectionAndBubble,
} from '../helpers';

export function canResolvePullRequest(
    pullRequest: AgentTaskPullRequestSummary,
    workItems: AgentTaskWorkItemSummary[],
) {
    const sourceWorkItem = workItems.find(
        workItem => workItem.workItemId === pullRequest.sourceWorkItemId,
    );
    const activeRepairExists = workItems.some(workItem =>
        workItem.prId === pullRequest.prId
        && workItem.workItemKind === 1
        && [0, 1, 2, 7].includes(workItem.jobStatus));
    return ![7, 8].includes(pullRequest.status)
        && sourceWorkItem !== undefined
        && [3, 4].includes(sourceWorkItem.jobStatus)
        && !activeRepairExists;
}

export function PullRequestSummaryList(props: {
    service: Service | undefined;
    pullRequests: AgentTaskPullRequestSummary[];
    workItems?: AgentTaskWorkItemSummary[];
    getDetailUrl?: (pullRequest: AgentTaskPullRequestSummary) => string;
    onOpen?: (pullRequest: AgentTaskPullRequestSummary) => void;
    onResolve?: (pullRequest: AgentTaskPullRequestSummary) => void;
    resolveDisabled?: boolean;
    resolvingPullRequestIds?: ReadonlySet<number>;
    nonActionablePullRequestIds?: ReadonlySet<number>;
}) {
    const {
        service,
        pullRequests,
        workItems = [],
        getDetailUrl,
        onOpen,
        onResolve,
        resolveDisabled = false,
        resolvingPullRequestIds,
        nonActionablePullRequestIds,
    } = props;

    if (pullRequests.length === 0) {
        return <>None</>;
    }

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            {pullRequests.map(pullRequest => {
                const adoUrl = buildAdoPullRequestUrl(service, pullRequest.prId);
                const detailUrl = getDetailUrl?.(pullRequest);
                const isResolving = resolvingPullRequestIds?.has(pullRequest.prId) === true;
                const wasAssessedNotActionable =
                    nonActionablePullRequestIds?.has(pullRequest.prId) === true;
                return (
                    <div key={pullRequest.prId}>
                        <span>
                            {getPullRequestStatusWithHealthLabel(
                                pullRequest.status,
                                pullRequest.health,
                                true,
                            )}
                        </span>
                        {' • '}
                        <a
                            href={detailUrl ?? adoUrl ?? '#'}
                            target={detailUrl ? undefined : '_blank'}
                            rel={detailUrl ? undefined : 'noopener noreferrer'}
                            onClick={onOpen ? event => {
                                event.preventDefault();
                                stopSelectionAndBubble(event);
                                onOpen(pullRequest);
                            } : undefined}
                            onMouseDown={onOpen ? stopSelectionAndBubble : undefined}
                            onPointerDown={onOpen ? stopSelectionAndBubble : undefined}
                        >
                            PR #{pullRequest.prId}
                        </a>
                        {onResolve
                            && !wasAssessedNotActionable
                            && canResolvePullRequest(pullRequest, workItems) && (
                            <>
                                {' • '}
                                <DefaultButton
                                    data-selection-disabled={true}
                                    text={isResolving ? 'Resolving...' : 'Resolve'}
                                    disabled={resolveDisabled || isResolving}
                                    onClick={event => {
                                        stopSelectionAndBubble(event);
                                        onResolve(pullRequest);
                                    }}
                                    onMouseDown={stopSelectionAndBubble}
                                    onPointerDown={stopSelectionAndBubble}
                                    styles={{
                                        root: {
                                            height: 24,
                                            minWidth: 0,
                                            padding: '0 8px',
                                        },
                                    }}
                                />
                            </>
                        )}
                    </div>
                );
            })}
        </Stack>
    );
}
